/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { useMemo } from 'preact/hooks';
import { useTranslationBundle } from '../hooks/UNSAFE_useTranslationBundle';
import { BundleType } from '../resources/nls/bundle';
import { Item } from '../utils/UNSAFE_dataProvider';
import { ItemTextType } from '../utils/UNSAFE_selectUtils';
import { renderItemText } from '../PRIVATE_SelectCommon';

export function useDisplayValues<K, D>(
  itemText: ItemTextType<K, D>,
  valueItems?: Item<K, D>[]
): string {
  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');
  const separator = translations.plural_separator();

  // create the text displayed in the field from the selected values
  const concatenatedDisplayValues = useMemo(() => {
    if (valueItems === undefined) {
      return '';
    }
    const displayValues = _createDisplayValues(itemText, valueItems);
    const concatDisplayValues = _concatDisplayValues(displayValues, separator);
    return concatDisplayValues;
  }, [valueItems, itemText]);

  return concatenatedDisplayValues;
}

function _concatDisplayValues(displayValues: Set<string>, separator: string) {
  return Array.from(displayValues.values()).join(separator);
}

function _createDisplayValues<K, D>(itemText: ItemTextType<K, D>, selectedItems?: Item<K, D>[]) {
  const arValues = selectedItems ? [...selectedItems?.values()] : [];
  const mapFunc = (itemContext: Item<K, D>) => renderItemText(itemContext, itemText);
  const mappedArray = arValues.map(mapFunc);
  return new Set<string>(mappedArray as string[]);
}
